import { makeStyles, Popover, PopoverProps } from '@material-ui/core';
import TouchRipple from '@material-ui/core/ButtonBase/TouchRipple';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../../theme/theme';

const useButtonClasses = makeStyles({
    root: {
        position: 'relative',
        display: 'flex',
        ':where(&)': {
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: `all ${theme.transitions.default}s`,
            ...theme.fonts.button,
            ':where(&:not(.icon))': { padding: '16px 32px' },
            ':where(&.icon)': { padding: 16 },
            ':where(&.icon.rounded)': { borderRadius: '50%' },
        },
        '& > svg': {
            minWidth: 20,
            minHeight: 20,
            width: 20,
            height: 20,
            margin: -1,
        },
    },
    menu: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 8,
        margin: 6,
    },
});

const useButtonThemeClasses = makeStyles({
    root: {
        ':where(&)': {
            backgroundColor: 'white',
            ':where(&[disabled])': { cursor: 'default' },
            ':where(&.open, &:not([disabled]):hover)': { filter: 'brightness(0.9)' },
            ':where(&:focus-visible)': { outline: 'none' },
        },
    },
    primary: {
        ':where(&)': {
            backgroundColor: theme.colors.darkBlue[5],
            color: 'white',
            ':where(&[disabled])': {
                backgroundColor: theme.colors.neutral[8],
                color: '#9E9E9E',
            },
        },
    },
    secondary: {
        ':where(&)': {
            backgroundColor: '#F1F5F6',
            color: '#283D4D',
        },
    },
    blue: {
        ':where(&)': { color: theme.colors.darkBlue[5] },
    },
    normal: {
        ':where(&)': { color: theme.colors.neutral[2] },
    },
    light: {
        ':where(&)': {
            color: theme.colors.neutral[2],
            ':where(&.open, &:not([disabled]):hover)': {
                filter: 'none',
                color: theme.colors.darkBlue[5],
            },
        },
    },
    lighter: {
        ':where(&)': {
            color: theme.colors.neutral[3],
            ':where(&.open, &:not([disabled]):hover)': {
                filter: 'none',
                color: theme.colors.darkBlue[5],
            },
        },
    },
    warning: {
        ':where(&)': {
            color: theme.colors.red[1],
        },
    },
});

/**
 * Native html button with a ripple effect and custom styles.
 * This button implementation offers utilities for displaying popup menus by providing a `menu` property
 * When a menu is open, the button will toggle an `.open` class on itself, that can be used for styling.
 *
 * @param theme the button theme, if not provided, not background or color is set
 * @param icon set the button style for the icon button mode, if `'rounded'`, the button uses rounded borders
 * @param menu content to render if the button is a menu button, clicking anywhere inside or outside the menu will
 *        close it, unless click events default actions are prevented using `event.preventDefault()`.
 * @param menuProps button menu properties, some properties cannot be overridden
 */
export const Button = ({
    theme,
    icon,
    menu,
    menuProps,
    ...rootProps
}: {
    theme?: keyof ReturnType<typeof useButtonThemeClasses>;
    icon?: boolean | 'rounded';
    menu?: React.ReactNode;
    menuProps?: Partial<PopoverProps>;
} & JSX.IntrinsicElements['button']) => {
    const classes = useButtonClasses();
    const themeClasses = useButtonThemeClasses();
    const root$ = React.useRef<HTMLElement>();
    const ripple = React.useRef<any>();
    const [open, setOpen] = React.useState(false);
    const cls = { [themeClasses[theme ?? 'primary']]: theme != undefined, icon, rounded: icon === 'rounded', open };

    return (
        <>
            <button
                ref={root$ as any}
                {...rootProps}
                className={clsx(classes.root, themeClasses.root, cls, rootProps.className)}
                onMouseDown={e => (ripple.current.start(e), rootProps.onMouseDown?.(e))}
                onMouseUp={e => (ripple.current.stop(e), rootProps.onMouseUp?.(e))}
                onFocus={e => (e.target.matches(':focus-visible') && ripple.current.pulsate(e), rootProps.onFocus?.(e))}
                onBlur={e => (ripple.current.stop(e), rootProps.onBlur?.(e))}
                onClick={e => (!!menu && setOpen(!e.defaultPrevented), rootProps.onClick?.(e))}
            >
                {rootProps.children}
                <TouchRipple ref={ripple} />
            </button>
            {menu && (
                <Popover
                    {...menuProps}
                    classes={{ ...menuProps?.classes, paper: classes.menu }}
                    open={open}
                    anchorEl={root$.current}
                    onClose={(e, r) => (setOpen(false), menuProps?.onClose?.(e, r))}
                    disableScrollLock
                    onClick={e => (setOpen(e.defaultPrevented), e.preventDefault(), menuProps?.onClick?.(e))}
                >
                    {menu}
                </Popover>
            )}
        </>
    );
};

const useButtonsClasses = makeStyles({
    root: {
        display: 'flex',
        gap: 1,
        ':where(& > button)': { borderRadius: 0 },
        ':where(& > button:first-child)': { borderRadius: '4px 0 0 4px' },
        ':where(& > button:last-child)': { borderRadius: '0 4px 4px 0' },
    },
});

/**
 * Buttons is a wrapper for a set of Button components.
 * This component apply some styling to make the buttons look like a single group.
 * This component is not intended to be used with children other than the Button component.
 *
 * @param buttonProps props to be applied to all buttons, they have lower precedence than the button s own props
 */
export const Buttons = (props: { buttonProps?: Parameters<typeof Button>[0] } & JSX.IntrinsicElements['div']) => {
    const { buttonProps, ...rootProps } = props;
    const classes = useButtonsClasses();

    return (
        <div {...rootProps} className={clsx(classes.root, props.className)}>
            {[props.children]
                .flat(Infinity)
                .map((button, i) => React.cloneElement(button, { key: i, ...buttonProps, ...button.props }))}
        </div>
    );
};
